import { z } from 'zod'

/**
 * Visão (SPEC-20261103-1418): o recorte de uma tela guardado com nome.
 *
 * Pedido do Cristian, 2026-11-03: "nas visões iremos colocar as configurações
 * de filtros, colunas e ordem de campos na lista e o tipo de visualização (...)
 * e essas visões devem poder ser privadas ou públicas ou compartilhadas com
 * quais colaboradores".
 *
 * Uma visão vive num QUADRO (tipos quadro, lista, planilha, linha do tempo) ou
 * na LISTA DE ITENS DE TRABALHO (lista e linha do tempo). A API guarda a
 * configuração sem interpretar: quem aplica o filtro é a tela.
 */

/** Tipo de visualização. */
export const modoDaVisaoSchema = z.enum(['quadro', 'lista', 'planilha', 'tempo'])
export type ModoDaVisao = z.infer<typeof modoDaVisaoSchema>

/** Os tipos que cada escopo tem. */
export const MODOS_DO_QUADRO: readonly ModoDaVisao[] = ['quadro', 'lista', 'planilha', 'tempo']
export const MODOS_DOS_ITENS: readonly ModoDaVisao[] = ['lista', 'tempo']

export const ROTULO_DO_MODO: Record<ModoDaVisao, string> = {
  quadro: 'Quadro',
  lista: 'Lista',
  planilha: 'Planilha',
  tempo: 'Linha do tempo',
}

export const ICONE_DO_MODO: Record<ModoDaVisao, string> = {
  quadro: 'fas fa-columns',
  lista: 'fas fa-list',
  planilha: 'fas fa-table',
  tempo: 'fas fa-stream',
}

/** Quem vê: só o dono, as pessoas escolhidas, ou todos que veem a tela. */
export const visibilidadeDaVisaoSchema = z.enum(['private', 'shared', 'public'])
export type VisibilidadeDaVisao = z.infer<typeof visibilidadeDaVisaoSchema>

/** 'viewer' = usar a visão; 'editor' = usar e alterar. */
export const papelNaVisaoSchema = z.enum(['viewer', 'editor'])
export type PapelNaVisao = z.infer<typeof papelNaVisaoSchema>

export const ordenacaoDaVisaoSchema = z.object({
  k: z.string().min(1).max(120),
  dir: z.enum(['asc', 'desc']),
})

export const configDaVisaoSchema = z.object({
  /** Ausente = a visão não troca o tipo de visualização de quem a escolhe. */
  modo: modoDaVisaoSchema.optional(),
  /**
   * O filtro, no formato da tela: `SavedFilter` (join + criteria) no quadro,
   * os critérios do filtro rápido na lista de Itens. Opaco aqui.
   */
  filtro: z.record(z.unknown()).nullable().optional(),
  /** Colunas da lista, na ordem; ausente = as colunas de quem abre. */
  colunas: z.array(z.string().min(1).max(120)).max(200).optional(),
  /** Ordenação da lista; null = a ordem natural. */
  ordenacao: ordenacaoDaVisaoSchema.nullable().optional(),
  /** Largura de cada coluna da lista (px), pela chave da coluna — SPEC-20261104-2147. */
  larguras: z.record(z.number().int().min(30).max(2000)).optional(),
  /** Lista de Itens: em árvore (true) ou lista simples (false) — SPEC-20261104-2147. */
  arvore: z.boolean().optional(),
  /** Período por data do cabeçalho (`Periodo` da tela); null = sem período. Opaco aqui. */
  periodo: z.record(z.unknown()).nullable().optional(),
  /** Filtro rápido do funil (`SavedFilter`); null = nenhum. Opaco aqui. */
  filtroRapido: z.record(z.unknown()).nullable().optional(),
})
export type ConfigDaVisao = z.infer<typeof configDaVisaoSchema>

export const compartilhamentoDaVisaoSchema = z.object({
  memberId: z.string().uuid(),
  role: papelNaVisaoSchema,
})
export type CompartilhamentoDaVisao = z.infer<typeof compartilhamentoDaVisaoSchema>

/** O que a API devolve de cada visão. */
export type Visao = {
  id: string
  name: string
  visibility: VisibilidadeDaVisao
  publicRole: PapelNaVisao
  config: ConfigDaVisao
  owner: { id: string; name: string }
  /** Só o dono recebe a lista; os outros recebem []. */
  shares: CompartilhamentoDaVisao[]
  /** É de quem pergunta. */
  mine: boolean
  /** Pode mudar nome, filtro, colunas, ordenação e tipo. */
  canEdit: boolean
  sortOrder: number
  /**
   * RASCUNHO de quem pergunta (SPEC-20261104-2147): o que mudou e ainda não foi
   * confirmado (✓) nem descartado (✗). null = a visão está como foi salva.
   */
  draft?: Partial<ConfigDaVisao> | null
  /** A visão mudou depois que o rascunho começou (outra pessoa confirmou). */
  draftStale?: boolean
  /** Pode confirmar o rascunho na visão: dono, ou administrador do workspace. */
  canCommit?: boolean
}

export const ROTULO_DA_VISIBILIDADE: Record<VisibilidadeDaVisao, string> = {
  private: 'Privada',
  shared: 'Compartilhada',
  public: 'Pública',
}

export const ICONE_DA_VISIBILIDADE: Record<VisibilidadeDaVisao, string> = {
  private: 'fas fa-lock',
  shared: 'fas fa-user-friends',
  public: 'fas fa-globe-americas',
}

/**
 * O papel de uma pessoa numa visão, ou null se ela não a vê. Regra única para
 * a API (que barra) e para a tela (que esconde o lápis).
 */
export function papelDe(
  visao: { ownerMemberId: string; visibility: VisibilidadeDaVisao; publicRole: PapelNaVisao },
  memberId: string,
  compartilhada: PapelNaVisao | null,
): 'owner' | PapelNaVisao | null {
  if (visao.ownerMemberId === memberId) return 'owner'
  // o papel nomeado vence o geral quando a visão é pública e a pessoa também foi escolhida
  if (visao.visibility === 'public') {
    return compartilhada === 'editor' || visao.publicRole === 'editor' ? 'editor' : 'viewer'
  }
  if (visao.visibility === 'shared') return compartilhada
  return null
}
